/**
 * Supplier profile tab (SPEC screen 2). Same create-or-replace shape as the
 * customer tab: `PUT` on first enable with the party aggregate ETag, merge
 * `PATCH` afterwards with the profile-scoped validator.
 *
 * The bank-details slot the SPEC reserves stays a labelled placeholder — the
 * contract publishes no supplier bank-account operation under partners.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { surfaceProblem } from "@/components/form";

import {
  archiveSupplierProfile,
  getSupplierProfile,
  putSupplierProfile,
  updateSupplierProfile,
  type SupplierProfile,
  type SupplierProfilePatch,
  type SupplierProfilePut,
} from "../api";
import { partnersKeys, usePaymentTerms, useSupplierGroups } from "../queries";

const CURRENCY_RE = /^[A-Z]{3}$/;

export interface SupplierProfilePanelProps {
  partyId: string;
  partyEtag: string | null;
  profile?: SupplierProfile;
  /** Party default currency — the sensible default purchase currency. */
  currency: string;
  partyEditable: boolean;
  canRead?: boolean;
  canUpdate?: boolean;
  canArchive?: boolean;
  canReadGroups?: boolean;
  canReadPaymentTerms?: boolean;
}

interface Draft {
  groupId: string;
  paymentTermsId: string;
  purchaseCurrency: string;
}

export function SupplierProfilePanel({
  partyId,
  partyEtag,
  profile,
  currency,
  partyEditable,
  canRead = true,
  canUpdate = true,
  canArchive = true,
  canReadGroups = true,
  canReadPaymentTerms = true,
}: SupplierProfilePanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const exists = profile !== undefined && profile.status === "active";
  const editable = canUpdate && partyEditable;

  const [draft, setDraft] = useState<Draft>(() => ({
    groupId: profile?.supplier_group_id ?? "",
    paymentTermsId: profile?.payment_terms_id ?? "",
    purchaseCurrency: profile?.default_purchase_currency ?? currency,
  }));
  const [errors, setErrors] = useState<Partial<Record<keyof Draft, string>>>({});
  const [archiveConfirm, setArchiveConfirm] = useState(false);

  useEffect(() => {
    setDraft({
      groupId: profile?.supplier_group_id ?? "",
      paymentTermsId: profile?.payment_terms_id ?? "",
      purchaseCurrency: profile?.default_purchase_currency ?? currency,
    });
    setErrors({});
  }, [profile, currency]);

  const groupsQuery = useSupplierGroups(canReadGroups);
  const termsQuery = usePaymentTerms(canReadPaymentTerms);
  const groups = (groupsQuery.data ?? []).filter(
    (group) => group.status === "active" || group.id === draft.groupId,
  );
  const terms = (termsQuery.data ?? []).filter(
    (term) => term.status === "active" || term.id === draft.paymentTermsId,
  );

  const profileEtagQuery = useQuery({
    queryKey: [...partnersKeys.parties.detail(partyId), "supplier-profile"],
    queryFn: () => getSupplierProfile(partyId),
    enabled: canRead && exists,
  });
  const ifMatch = profileEtagQuery.data?.etag ?? partyEtag;

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: partnersKeys.parties.all });
  };

  const enableMutation = useMutation({
    mutationFn: (input: SupplierProfilePut) => putSupplierProfile(partyId, input, ifMatch ?? ""),
    onSuccess: invalidate,
    onError: (error) => surfaceProblem(error, t),
  });

  const patchMutation = useMutation({
    mutationFn: (patch: SupplierProfilePatch) =>
      updateSupplierProfile(partyId, patch, ifMatch ?? ""),
    onSuccess: invalidate,
    onError: (error) => surfaceProblem(error, t),
  });

  const archiveMutation = useMutation({
    mutationFn: () => archiveSupplierProfile(partyId),
    onSuccess: () => {
      setArchiveConfirm(false);
      invalidate();
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const save = (): void => {
    const next: Partial<Record<keyof Draft, string>> = {};
    if (draft.groupId === "") next.groupId = t("partners.supplier.groupRequired");
    if (draft.paymentTermsId === "") next.paymentTermsId = t("partners.supplier.termsRequired");
    const purchaseCurrency = draft.purchaseCurrency.trim().toUpperCase();
    if (!CURRENCY_RE.test(purchaseCurrency)) {
      next.purchaseCurrency = t("partners.supplier.currencyRequired");
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    if (!exists) {
      enableMutation.mutate({
        supplier_group_id: draft.groupId,
        payment_terms_id: draft.paymentTermsId,
        default_purchase_currency: purchaseCurrency,
      });
      return;
    }
    patchMutation.mutate({
      supplier_group_id: draft.groupId,
      payment_terms_id: draft.paymentTermsId,
      default_purchase_currency: purchaseCurrency,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3">
          <CardTitle>
            <h2>{t("partners.supplier.title")}</h2>
          </CardTitle>
          {exists && canArchive && partyEditable ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setArchiveConfirm(true)}
            >
              {t("partners.supplier.disable")}
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {!exists ? (
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium">{t("partners.supplier.notEnabled")}</p>
              <p className="text-sm text-muted-foreground">{t("partners.supplier.enableHint")}</p>
            </div>
          ) : null}

          {!editable ? (
            <p className="text-sm text-muted-foreground">{t("partners.supplier.readOnly")}</p>
          ) : null}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="supplier-group">{t("partners.supplier.group")}</Label>
              <Select
                value={draft.groupId}
                disabled={!editable || !canReadGroups}
                onValueChange={(value) => {
                  setDraft((current) => ({ ...current, groupId: value }));
                  setErrors((current) => ({ ...current, groupId: undefined }));
                }}
              >
                <SelectTrigger id="supplier-group" aria-invalid={errors.groupId !== undefined}>
                  <SelectValue placeholder={t("partners.common.none")} />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.groupId !== undefined ? (
                <p role="alert" className="text-sm text-destructive">
                  {errors.groupId}
                </p>
              ) : null}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="supplier-terms">{t("partners.supplier.paymentTerms")}</Label>
              <Select
                value={draft.paymentTermsId}
                disabled={!editable || !canReadPaymentTerms}
                onValueChange={(value) => {
                  setDraft((current) => ({ ...current, paymentTermsId: value }));
                  setErrors((current) => ({ ...current, paymentTermsId: undefined }));
                }}
              >
                <SelectTrigger
                  id="supplier-terms"
                  aria-invalid={errors.paymentTermsId !== undefined}
                >
                  <SelectValue placeholder={t("partners.common.none")} />
                </SelectTrigger>
                <SelectContent>
                  {terms.map((term) => (
                    <SelectItem key={term.id} value={term.id}>
                      {term.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.paymentTermsId !== undefined ? (
                <p role="alert" className="text-sm text-destructive">
                  {errors.paymentTermsId}
                </p>
              ) : null}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="supplier-currency">{t("partners.supplier.purchaseCurrency")}</Label>
              <Input
                id="supplier-currency"
                dir="ltr"
                maxLength={3}
                autoComplete="off"
                value={draft.purchaseCurrency}
                disabled={!editable}
                aria-invalid={errors.purchaseCurrency !== undefined}
                onChange={(event) => {
                  const value = event.target.value.toUpperCase();
                  setDraft((current) => ({ ...current, purchaseCurrency: value }));
                  setErrors((current) => ({ ...current, purchaseCurrency: undefined }));
                }}
              />
              {errors.purchaseCurrency !== undefined ? (
                <p role="alert" className="text-sm text-destructive">
                  {errors.purchaseCurrency}
                </p>
              ) : null}
            </div>
          </div>

          {editable ? (
            <div>
              <Button
                type="button"
                loading={enableMutation.isPending || patchMutation.isPending}
                onClick={save}
              >
                {exists ? t("partners.supplier.save") : t("partners.supplier.enable")}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("partners.supplier.bankTitle")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {t("partners.supplier.bankBody")}
        </CardContent>
      </Card>

      <Dialog open={archiveConfirm} onOpenChange={setArchiveConfirm}>
        <DialogContent closeLabel={t("actions.close")}>
          <DialogHeader>
            <DialogTitle>{t("partners.supplier.disableTitle")}</DialogTitle>
            <DialogDescription>{t("partners.supplier.disableBody")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setArchiveConfirm(false)}>
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              loading={archiveMutation.isPending}
              onClick={() => archiveMutation.mutate()}
            >
              {t("partners.supplier.disable")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
